import { IIconProps } from "@fluentui/react";
import { useEffect } from "react";
import { Protocol } from "../../../models/Entities/Protocols/Protocol";
import { ErrorType } from "../../../models/HttpError";

// stores
import * as SettlementStore from '../../../redux/middleware/SettlementMiddleware';
import * as ContractStore from '../../../redux/middleware/ContractMiddleware';
import * as MedicalProtocolStore from '../../../redux/middleware/MedicalProtocolMiddleware';
import * as SponsorStore from '../../../redux/middleware/SponsorMiddleware';
import * as PractitionerStore from '../../../redux/middleware/PractitionerMiddleware';
import * as MedicalInsuranceStore from '../../../redux/middleware/MedicalInsuranceMiddleware';

//State
import { AppState } from "../../../redux/reducers";
import { Settlement } from "../../../models/Entities/Settlements/Settlement";
import { Sponsor } from "../../../models/Entities/Sponsor/Sponsor";
import { Practitioner } from "../../../models/Entities/Practitioners/Practitioner";
import { MedicalInsurance } from "../../../models/Entities/MedicalInsurances/MedicalInsurance";
import { AzureFileOwner } from "../../../models/Entities/AzureFiles/AzureFileUpload";
import { StorageOwnerTypeEnum } from "../../../models/Enums/AzureFileTypeEnum";
import { providerOf, resolveSettlementKind } from "../../../functions/SettlementKind.Functions";
import { getProviderStorageOwner } from "../../../functions/AzureFileUpload.Functions";

/** Contraparte que muestra la cabecera (spec 010 suma el proveedor). */
export type SettlementEntityType = 'sponsor' | 'practitioner' | 'agreement' | 'provider';

interface IProps {
    SettlementId: number;
    /** When present, shows the "upload invoice" button (opens the Azure upload drawer) */
    onUploadInvoice?: () => void;
    /** Already translated reason; when present the upload button is disabled and shows it as tooltip */
    uploadInvoiceDisabledReason?: string;
}
  
  interface ConnectedProps {
    isLoading: boolean;
    isUpdating: boolean;
    isUpdatedSuccessfully: boolean;
    isLoadingSuccessfully: boolean;
    failOnUpdating: boolean;
    failOnLoading: boolean;
    isChangingStatus: boolean;
    changeStatusSuccessfully: boolean;
    failOnChangeStatus: boolean;
    error: ErrorType | undefined;
    Settlement: Settlement | undefined;
    settlementEntityLabel: string | undefined;
    settlementEntityType: SettlementEntityType | undefined;
        accessToken: string | undefined;
  }
  
  interface DispatchProps {
    GetSettlementById: typeof SettlementStore.actionCreators.GetSettlementById;
    ChangeSettlementStatus: typeof SettlementStore.actionCreators.ChangeSettlementStatus;
    ResetSettlementChangeStatusFlags: typeof SettlementStore.actionCreators.ResetSettlementChangeStatusFlags;
    GetMedicalProtocolById: typeof MedicalProtocolStore.actionCreators.GetMedicalProtocolById;
    GetContractById: typeof ContractStore.actionCreators.GetContractById;
    GetSponsorById: typeof SponsorStore.actionCreators.GetSponsorById;
    GetPractitionerById: typeof PractitionerStore.actionCreators.GetPractitionerById;
    GetMedicalInsuranceById: typeof MedicalInsuranceStore.actionCreators.GetMedicalInsuranceById;
  }
  
  export type Props = IProps & ConnectedProps & DispatchProps;
  
  export const mapStateToProps = (state: AppState, ownProps: IProps) => {

    const currentSettlement: Settlement | undefined = state.Settlement?.list?.find((item: Settlement) => item.id === ownProps.SettlementId );

    return {
            accessToken: state.oidc?.user?.access_token,
            isLoading: state.Settlement?.isLoadingAll,
            isUpdating: state.Settlement?.isLoadingAll,
            isUpdatedSuccessfully: state.Settlement?.successLoadingAll,
            isLoadingSuccessfully: state.Settlement?.successLoadingAll,
            failOnUpdating: state.Settlement?.failOnLoadingAll,
            failOnLoading: state.Settlement?.failOnLoadingAll,
            isDeleting: state.Settlement?.isDeletingOne,
            isDeletedSuccessfully: state.Settlement?.successDeletingOne,
            failOnDeleting: state.Settlement?.failOnDeletingOne,
            isChangingStatus: currentSettlement?.isChangingStatus,
            changeStatusSuccessfully: currentSettlement?.successChangingStatus,
            failOnChangeStatus: currentSettlement?.failOnChangeStatus,
            error: currentSettlement?.error,
            Settlement: currentSettlement,
            ...(() => {
                if (!currentSettlement) return { settlementEntityLabel: undefined, settlementEntityType: undefined };
                const kind = resolveSettlementKind(currentSettlement);
                if (kind === 'provider') {
                    // La razón social viaja en la asociación (contrato §5): no hace falta pedir el proveedor.
                    const businessName = providerOf(currentSettlement)?.businessName?.trim();
                    return { settlementEntityLabel: businessName || undefined, settlementEntityType: 'provider' as const };
                }
                if (kind === 'sponsor') {
                    const sponsor: Sponsor | undefined = state.Sponsor?.list?.find((s: Sponsor) => s.id === currentSettlement.sponsor[0]?.sponsorId);
                    const name = sponsor
                        ? (sponsor.person?.businessName || [sponsor.person?.firstName, sponsor.person?.lastName].filter(Boolean).join(' '))
                        : undefined;
                    return { settlementEntityLabel: name, settlementEntityType: 'sponsor' as const };
                }
                if (kind === 'practitioner') {
                    const practitionerId = settlementPractitionerId(currentSettlement);
                    const prac: Practitioner | undefined = state.Practitioners?.list?.find((p: Practitioner) => p.userId === practitionerId);
                    return { settlementEntityLabel: prac?.fullName, settlementEntityType: 'practitioner' as const };
                }
                if (kind === 'agreement') {
                    const mi: MedicalInsurance | undefined = state.MedicalInsurances?.list?.find((m: MedicalInsurance) => m.id === currentSettlement.medicalInsuranceId);
                    return { settlementEntityLabel: mi?.businessName || mi?.title, settlementEntityType: 'agreement' as const };
                }
                return { settlementEntityLabel: undefined, settlementEntityType: undefined };
            })()
        }
    };
  
  /**
   * El API no manda `practitionerId` en la raíz de la liquidación: viene en la
   * asociación. Se mira primero la raíz por si alguna vez llega, y se cae a la
   * asociación, que es lo que hay hoy.
   */
  export const settlementPractitionerId = (settlement: Pick<Settlement, 'practitionerId' | 'practitioner'> | undefined): string | undefined =>
    settlement?.practitionerId || settlement?.practitioner?.find((association) => !!association?.practitionerId)?.practitionerId || undefined;

  /**
   * Titular de la carpeta del storage para las facturas de la liquidación (contrato §2):
   * misma precedencia que `settlementEntityType` (`resolveSettlementKind`) → proveedor (tipo 4), sponsor
   * (+ protocolo), médico, obra social. Devuelve undefined si faltan los datos (el botón de subida queda deshabilitado).
   */
  export const getSettlementStorageOwner = (
    settlement: (Pick<Settlement, 'sponsor' | 'practitioner' | 'protocol' | 'agreements' | 'practitionerId' | 'medicalInsuranceId'>
      & Partial<Pick<Settlement, 'settlementType' | 'provider'>>) | undefined
  ): AzureFileOwner | undefined => {
    if (!settlement) return undefined;
    const kind = resolveSettlementKind(settlement);
    if (kind === 'provider') {
      // Carpeta del proveedor (spec 010): ownerId = Provider.id.
      return getProviderStorageOwner(providerOf(settlement)?.providerId);
    }
    if (kind === 'sponsor') {
      const sponsorId = settlement.sponsor[0]?.sponsorId;
      const protocolId = settlement.protocol?.find((association) => !!association?.protocolId)?.protocolId;
      return sponsorId && protocolId
        ? { ownerType: StorageOwnerTypeEnum.Sponsor, ownerId: String(sponsorId), protocolId }
        : undefined;
    }
    if (kind === 'practitioner') {
      const practitionerId = settlementPractitionerId(settlement);
      return practitionerId
        ? { ownerType: StorageOwnerTypeEnum.Practitioner, ownerId: practitionerId }
        : undefined;
    }
    if (kind === 'agreement') {
      return settlement.medicalInsuranceId
        ? { ownerType: StorageOwnerTypeEnum.MedicalInsurance, ownerId: String(settlement.medicalInsuranceId) }
        : undefined;
    }
    return undefined;
  };

  /** Nombre del PDF descargado: el de la liquidación sin caracteres que el sistema de archivos no acepta. */
  export const settlementPdfFileName = (name: string | undefined, settlementId: number): string => {
    const safeName = (name ?? '').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
    return `liquidacion-${safeName || settlementId}.pdf`;
  };

  export const mapDispatchToProps = {
    ...SettlementStore.actionCreators,
    ...MedicalProtocolStore.actionCreators,
    ...ContractStore.actionCreators,
    GetSponsorById: SponsorStore.actionCreators.GetSponsorById,
    GetPractitionerById: PractitionerStore.actionCreators.GetPractitionerById,
    GetMedicalInsuranceById: MedicalInsuranceStore.actionCreators.GetMedicalInsuranceById,
  };

    // Calls GetSettlementById when changeStatusSuccessfully flips to true
    export const useSettlementStatusRefresh = (
        settlementId: number | undefined,
        changeStatusSuccessfully: boolean | undefined,
        GetSettlementById: DispatchProps["GetSettlementById"]
    ) => {
        useEffect(() => {
            if (settlementId && changeStatusSuccessfully) {
                GetSettlementById(settlementId);
            }
        }, [settlementId, changeStatusSuccessfully, GetSettlementById]);
    };

  /*
    Contract Status
    _____________________
    Active = 1,
    Inactive = 2,
    Suspended = 3,
    Expired = 4,
    Canceled = 5,
    Terminated = 6
*/

export const GetSettlementStatusDescripcion = (status: number | undefined) => {
    switch (status) {
      case undefined:
          return 'SettlementStatus.pending'; // "Pendiente";
        case 1:
            return 'SettlementStatus.registered'; // "Registrado";
        case 2:
            return 'SettlementStatus.inProcess'; // "En proceso";
        case 3:
            return 'SettlementStatus.finished'; // "Finalizado";
        case 4:
            return 'SettlementStatus.cancelled'; // "Cancelado";
        case 5:
            return 'SettlementStatus.Confirmed'; // "Conformado";
        case 6:	
            return 'SettlementStatus.rejected'; // "Rechazado";
        case 7:
            return 'SettlementStatus.fail'; // "Fallido";
        case 8:
            return 'SettlementStatus.paid'; // "Pagado";
        case 9:
            return 'SettlementStatus.debtUnpaid'; // "Deuda impaga";
        default:
        return 'SettlementStatus.pending'; // "Pendiente";
    }
  };

  export const GetContractStatusClass = (status: number | undefined) => {
    switch (status) {
        case undefined:
            return "";
        case 1:
            return "Active";
        case 2:
            return "Inactive";
        case 3:
            return "Suspended";
        case 4:
            return "Expired";
        case 5:
            return "Canceled";
        case 6:
            return "Terminated";
        default:
        return "";
  }
};

export const GetSettlementStatusIcon = (status: number | undefined) => {
    switch (status) {
        case undefined:
            return 'WaitlistConfirmMirrored';
        case 1:
            return 'WaitlistConfirmMirrored';
        case 2:
            return 'SyncStatus';
        case 3:
            return 'SkypeCircleCheck';
        case 4:
            return 'StatusErrorFull';
        case 5:
            return 'Completed';    // Confirmado
        case 6:	
            return 'StatusErrorFull';
        case 7:
            return 'ErrorBadge';
        case 8:
            return 'PaymentCard';  // Pagado
        case 9:
            return 'Warning';
        default:
        return 'WaitlistConfirmMirrored';
    }
  }


  //#region Icons
export const PropertiesIcon: IIconProps = { iconName: 'DietPlanNotebook' };
export const AddIcon: IIconProps = { iconName: 'CirclePlus' };
export const ViewIcon: IIconProps = { iconName: 'View' };
export const LinkContractIcon: IIconProps = { iconName: 'Link' };